"use client";

import { useState } from "react";

import clsx from "clsx";

import { useTranslation } from "~/app/hooks/useTranslation";
import InstagramSvg from "~/assets/images/hospitality/instagram.svg?react";
import Img1 from "~/assets/images/hospitality/sectionBook-img1.png";
import WhatsAppSvg from "~/assets/images/hospitality/whatsapp.svg?react";
import { submitDemoRequest } from "~/common/api/demoRequestClient";
import Breadcrumb from "~/common/components/Breadcrumb";
import Image from "~/common/components/CustomImage";
import Modal from "~/common/components/Modal";
import PhoneInputButton from "~/common/components/PhoneInputButton";
import SectionWrapper from "~/common/components/SectionWrapper";
import { formatPhoneInput, getDefaultPhoneCountry, isPhoneValid, normalizePhoneNumber } from "~/common/utils/phone";

import styles from "./SectionBook.module.scss";

export default function SectionBook({ locale }) {
  const { t } = useTranslation(locale);
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState(() => getDefaultPhoneCountry(locale));

  function handlePhoneChange(e) {
    const formatted = formatPhoneInput(e.target.value, phoneCountry);
    setPhone(formatted);
    if (error) setError(null);
  }

  function handleCountryChange(nextCountry) {
    setPhoneCountry(nextCountry);
    if (phone) {
      setPhone(formatPhoneInput(phone, nextCountry));
    }
    if (error) setError(null);
  }

  async function onSubmit() {
    if (loading) return;
    setError(null);
    const normalizedPhone = normalizePhoneNumber(phone, phoneCountry);
    if (!normalizedPhone) {
      setError(t("common.errors.required"));
      return;
    }
    if (!isPhoneValid(phone, phoneCountry)) {
      setError(t("common.errors.invalidPhone"));
      return;
    }
    try {
      setLoading(true);
      const payload = {
        context: "government-section-book",
        appLocale: locale,
      };
      await submitDemoRequest({ phone: normalizedPhone, payload });
      setModalOpen(true);
      setPhone("");
    } catch (e) {
      setError(e.message || "Error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <SectionWrapper background={"/hospitality/hero1.svg"} className={styles.section}>
      <Modal
        idPrefix="demo-request-modal-government"
        title={t("common.thankYou")}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <button btnType="default" onClick={() => setModalOpen(false)}>
            {t("common.ok")}
          </button>
        }
      >
        <p>{t("section1.demoModal.content")}</p>
      </Modal>
      <div className={clsx(styles.breadcrumb, "card")}>
        <Breadcrumb
          segments={[
            t("solutions.breadcrumb.solutions"),
            t("solutions.breadcrumb.byIndustry"),
            t("solutions.breadcrumb.government"),
          ]}
        />
      </div>
      <div className={clsx(styles.container, "card")}>
        <div className={styles.content}>
          <h3 className={styles.pageName}>{t("solutions.breadcrumb.government")}</h3>
          <h1 className={clsx("pal-section-title", styles.title)} title={t("solutions.government.sectionBook.title")}>{t("solutions.government.sectionBook.title")}</h1>
          <p className={clsx("pal-section-desc", styles.desc)}>{t("solutions.government.sectionBook.description")}</p>
          <div className={styles.inputWrapper}>
            <PhoneInputButton
              locale={locale}
              placeholder={t("common.whatsappPlaceholder")}
              label={loading ? t("common.submitting") || "..." : t("common.bookADemo")}
              className={styles.inputButton}
              value={phone}
              onChange={handlePhoneChange}
              onSubmit={onSubmit}
              disabled={loading}
              inputProps={{ type: "tel", inputMode: "tel", autoComplete: "tel" }}
              country={phoneCountry}
              onCountryChange={handleCountryChange}
            />
            {error && (
              <p style={{ marginRight: "auto", marginLeft: 20, color: "#c00", fontSize: 12, marginTop: 6 }}>{error}</p>
            )}
          </div>
          <div className={styles.brands}>
            <WhatsAppSvg alt="WhatsApp" />
            <InstagramSvg alt="Instagram" />
          </div>
        </div>
        <div className={styles.image}>
          <Image
            src={Img1}
            alt={t("solutions.government.sectionBook.imageAlt")}
            width={Img1.width}
            height={Img1.height}
          />
        </div>
      </div>
    </SectionWrapper>
  );
}

